import { type ReactElement } from 'react'
import { useTheme, type Theme } from '@/hooks/useTheme'

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function SunIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  )
}

function MonitorIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Segment data
// ---------------------------------------------------------------------------

const SEGMENTS: { value: Theme; label: string; Icon: () => ReactElement }[] = [
  { value: 'light', label: 'Light mode', Icon: SunIcon },
  { value: 'system', label: 'Use system preference', Icon: MonitorIcon },
  { value: 'dark', label: 'Dark mode', Icon: MoonIcon },
]

// ---------------------------------------------------------------------------
// ThemeToggle
// ---------------------------------------------------------------------------

/**
 * Compact 3-segment control: Light / System / Dark.
 * Renders as an icon-only pill so it fits in the TopBar without taking much space.
 */
export function ThemeToggle() {
  const { theme, setTheme } = useTheme()

  return (
    <div
      role="group"
      aria-label="Color theme"
      className="flex items-center rounded-lg p-0.5 gap-0.5"
      style={{
        backgroundColor: 'var(--bg-elevated)',
        border: '1px solid var(--border-primary)',
      }}
    >
      {SEGMENTS.map(({ value, label, Icon }) => {
        const active = theme === value
        return (
          <button
            key={value}
            type="button"
            onClick={() => setTheme(value)}
            aria-label={label}
            aria-pressed={active}
            className="flex items-center justify-center w-7 h-7 rounded-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-purple-500"
            style={{
              color: active ? 'var(--accent-light)' : 'var(--text-muted)',
              backgroundColor: active ? 'var(--bg-card)' : 'transparent',
              boxShadow: active ? '0 1px 3px rgba(0,0,0,0.3)' : 'none',
            }}
          >
            <Icon />
          </button>
        )
      })}
    </div>
  )
}
